import { collection, doc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { db, hasFirebaseConfig } from '../lib/firebase';
import { calculateEarnedXp } from '../lib/trainingRewards';
import type { SessionMode, TrainingMode } from '../types';

const TRAINING_SESSIONS_STORAGE_PREFIX = 'vm-training-sessions:v1:';

export interface TrainingSession {
  id: string;
  userId: string;
  mode: TrainingMode;
  sessionMode: SessionMode;
  accuracy: number;
  durationMinutes: number;
  verbsLearned: number;
  xpEarned: number;
  completedAt: Date;
}

interface SaveTrainingSessionInput {
  userId: string;
  mode: TrainingMode;
  sessionMode: SessionMode;
  accuracy: number;
  durationMinutes: number;
  verbsLearned: number;
  xpEarned: number;
  id?: string;
  completedAt?: Date;
}

function getSessionStorageKey(userId: string) {
  return `${TRAINING_SESSIONS_STORAGE_PREFIX}${userId}`;
}

function toDate(value: unknown): Date | null {
  if (!value) return null;

  if (value instanceof Date) return value;

  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const maybe = value as { toDate?: () => Date };
    if (typeof maybe.toDate === 'function') {
      return maybe.toDate();
    }
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function resolveDurationMinutes(data: Record<string, unknown>): number {
  if (data.durationMinutes !== undefined) return toNumber(data.durationMinutes, 0);
  if (data.totalMinutes !== undefined) return toNumber(data.totalMinutes, 0);
  if (data.totalTimeMinutes !== undefined) return toNumber(data.totalTimeMinutes, 0);

  if (data.totalTimeMs !== undefined) {
    return Math.max(0, Math.round(toNumber(data.totalTimeMs, 0) / 60000));
  }

  if (data.totalTimeSec !== undefined) {
    return Math.max(0, Math.round(toNumber(data.totalTimeSec, 0) / 60));
  }

  return 0;
}

export function createTrainingSessionId(input: Pick<SaveTrainingSessionInput, 'userId' | 'mode' | 'sessionMode'> & { completedAt: Date }) {
  return `session-${input.userId}-${input.completedAt.getTime()}-${input.mode}-${input.sessionMode}`;
}

function createSessionFingerprint(session: TrainingSession) {
  return [
    session.userId,
    session.mode,
    session.sessionMode,
    session.accuracy,
    session.durationMinutes,
    session.verbsLearned,
    session.xpEarned,
    session.completedAt.toISOString(),
  ].join('|');
}

function sortTrainingSessions(sessions: TrainingSession[]) {
  return [...sessions].sort((left, right) => {
    const timeDiff = right.completedAt.getTime() - left.completedAt.getTime();
    if (timeDiff !== 0) {
      return timeDiff;
    }

    return left.id.localeCompare(right.id);
  });
}

function parseStoredSessions(userId: string): TrainingSession[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(getSessionStorageKey(userId));
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as Array<Omit<TrainingSession, 'completedAt'> & { completedAt: string }>;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return sortTrainingSessions(
      parsed
        .map((session) => ({
          ...session,
          completedAt: new Date(session.completedAt),
        }))
        .filter((session) => !Number.isNaN(session.completedAt.getTime())),
    );
  } catch (error) {
    console.error('Failed to parse stored training sessions:', error);
    return [];
  }
}

function persistStoredSessions(userId: string, sessions: TrainingSession[]) {
  if (typeof window === 'undefined') {
    return;
  }

  const serialized = sortTrainingSessions(sessions).map((session) => ({
    ...session,
    completedAt: session.completedAt.toISOString(),
  }));

  window.localStorage.setItem(getSessionStorageKey(userId), JSON.stringify(serialized));
}

function mergeTrainingSessions(...collections: TrainingSession[][]) {
  const sessionsById = new Map<string, TrainingSession>();
  const fingerprints = new Set<string>();

  collections.forEach((sessions) => {
    sessions.forEach((session) => {
      if (sessionsById.has(session.id)) {
        return;
      }

      const fingerprint = createSessionFingerprint(session);
      if (fingerprints.has(fingerprint)) {
        return;
      }

      sessionsById.set(session.id, session);
      fingerprints.add(fingerprint);
    });
  });

  return sortTrainingSessions(Array.from(sessionsById.values()));
}

function getMissingTrainingSessions(source: TrainingSession[], reference: TrainingSession[]) {
  const referenceIds = new Set(reference.map((session) => session.id));
  const referenceFingerprints = new Set(reference.map(createSessionFingerprint));

  return source.filter((session) => {
    if (referenceIds.has(session.id)) {
      return false;
    }

    return !referenceFingerprints.has(createSessionFingerprint(session));
  });
}

function buildTrainingSession(input: SaveTrainingSessionInput): TrainingSession {
  const completedAt = input.completedAt ?? new Date();
  const id = input.id ?? createTrainingSessionId({
    userId: input.userId,
    mode: input.mode,
    sessionMode: input.sessionMode,
    completedAt,
  });

  return {
    id,
    userId: input.userId,
    mode: input.mode,
    sessionMode: input.sessionMode,
    accuracy: input.accuracy,
    durationMinutes: input.durationMinutes,
    verbsLearned: input.verbsLearned,
    xpEarned: input.xpEarned,
    completedAt,
  };
}

async function fetchTrainingSessionsFromCloud(userId: string): Promise<TrainingSession[]> {
  if (!hasFirebaseConfig || !db || !userId) return [];

  const firestore = db;
  const sessionsQuery = query(collection(firestore, 'sessions'), where('userId', '==', userId));
  const snapshot = await getDocs(sessionsQuery);

  return sortTrainingSessions(
    snapshot.docs.map((sessionDoc) => {
      const data = sessionDoc.data() as Record<string, unknown>;

      const completedAt =
        toDate(data.completedAt) ??
        toDate(data.createdAt) ??
        new Date(0);

      const mode = (data.mode ?? 'typing') as TrainingMode;
      const sessionMode = (data.sessionMode ?? 'training') as SessionMode;

      return {
        id: sessionDoc.id,
        userId,
        mode,
        sessionMode,
        accuracy: toNumber(data.accuracy, 0),
        durationMinutes: resolveDurationMinutes(data),
        verbsLearned: toNumber(data.verbsLearned, 0),
        xpEarned: toNumber(data.xpEarned, calculateEarnedXp(mode, toNumber(data.accuracy, 0))),
        completedAt,
      };
    }),
  );
}

async function syncTrainingSessionsToCloud(userId: string, sessions: TrainingSession[]): Promise<void> {
  if (!hasFirebaseConfig || !db || !userId || sessions.length === 0) {
    return;
  }

  const firestore = db;

  await Promise.all(
    sessions.map((session) =>
      setDoc(
        doc(firestore, 'sessions', session.id),
        {
          id: session.id,
          userId: session.userId,
          mode: session.mode,
          sessionMode: session.sessionMode,
          accuracy: session.accuracy,
          durationMinutes: session.durationMinutes,
          verbsLearned: session.verbsLearned,
          xpEarned: session.xpEarned,
          completedAt: session.completedAt.toISOString(),
        },
        { merge: true },
      ),
    ),
  );
}

export async function saveTrainingSession(input: SaveTrainingSessionInput): Promise<TrainingSession> {
  const session = buildTrainingSession(input);
  const localSessions = parseStoredSessions(session.userId);
  const nextLocalSessions = mergeTrainingSessions([session], localSessions);

  persistStoredSessions(session.userId, nextLocalSessions);

  if (hasFirebaseConfig) {
    try {
      await syncTrainingSessionsToCloud(session.userId, [session]);
    } catch (error) {
      console.error('Failed to sync training session to Firestore, kept locally:', error);
    }
  }

  return session;
}

export async function fetchTrainingSessions(userId: string): Promise<TrainingSession[]> {
  if (!userId) return [];

  const localSessions = parseStoredSessions(userId);

  if (!hasFirebaseConfig) {
    return sortTrainingSessions(localSessions);
  }

  try {
    const remoteSessions = await fetchTrainingSessionsFromCloud(userId);
    const mergedSessions = mergeTrainingSessions(remoteSessions, localSessions);

    persistStoredSessions(userId, mergedSessions);

    const sessionsToSync = getMissingTrainingSessions(mergedSessions, remoteSessions);
    if (sessionsToSync.length > 0) {
      await syncTrainingSessionsToCloud(userId, sessionsToSync);
    }

    return mergedSessions;
  } catch (error) {
    console.error('Failed to load remote sessions, using local cache:', error);
    return sortTrainingSessions(localSessions);
  }
}
